<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'

import { STEPS_SETTINGS } from './StepCardData'

import BoxComponent from '@/ui-library/atoms/BoxComponent/BoxComponent.vue'
import FlexComponent from '@/ui-library/atoms/FlexComponent/FlexComponent.vue'
import TypographyComponent from '@/ui-library/atoms/TypographyComponent/TypographyComponent.vue'

const store = useStore()
const route = useRouter()

const currentCardId = computed(() => store.getters['onboarding/currentOnboardingCard'])
const currentStep = computed(() => store.getters['onboarding/currentOnboardingStep'])
const isOnboardingActive = computed(() => store.getters['onboarding/isOnboardingActive'])

const currentCard = computed(() => STEPS_SETTINGS.find((step) => step.id === currentCardId.value))
const currentCardPosition = ref<{ top?: string; left?: string }>({})

const setNextCard = async () => {
  if (currentCard.value.finishOnboarding) {
    await store.dispatch('onboarding/setCurrentStep', currentStep.value + 1)
    await store.dispatch('onboarding/setOnboardingActivity', false)
    return
  }

  if (currentCard.value.nextStepSwitch) {
    await store.dispatch('onboarding/setCurrentStep', currentStep.value + 1)
  }

  // Sometimes we need to hide cards until user moves to the next page or performs an action without changing the card,
  // because some other action will change it
  if (currentCard.value.hideCards) {
    await store.dispatch('onboarding/setOnboardingActivity', false)
  } else {
    const currentId = STEPS_SETTINGS.findIndex((card) => card.id === currentCardId.value)
    await store.dispatch('onboarding/setCurrentCard', STEPS_SETTINGS[currentId + 1].id)
  }
}

const calculatePosition = (elementPosition: { x: number; y: number }) => {
  const bodyScrollTop = document.documentElement.scrollTop || document.body.scrollTop || 0
  const bodyScrollLeft = document.documentElement.scrollLeft || document.body.scrollLeft || 0
  currentCardPosition.value.left = `${elementPosition.x + bodyScrollLeft + currentCard.value.offset.left}px`
  currentCardPosition.value.top = `${elementPosition.y + bodyScrollTop + currentCard.value.offset.top}px`
}

onMounted(() => {
  const elementPosition = document.getElementById(STEPS_SETTINGS[0].id)?.getBoundingClientRect()
  if (elementPosition) {
    calculatePosition(elementPosition)
  }
})

watch(
  [currentCardId, isOnboardingActive],
  async () => {
    await nextTick(() => {
      const elementPosition = document.getElementById(currentCardId.value)?.getBoundingClientRect()
      if (elementPosition && isOnboardingActive) {
        calculatePosition({ x: elementPosition.left, y: elementPosition.top })
      }
    })
  },
  { immediate: true, flush: 'post' },
)
</script>

<template>
  <div
    :class="[
      'card',
      {
        'is-fixed': currentCard.isFixed,
        'is-hidden': !isOnboardingActive || (currentCard.showOnRoute && route.currentRoute.value.name !== currentCard.showOnRoute),
      },
    ]"
    :style="{
      width: `${currentCard.width}px`,
      top: currentCardPosition.top,
      left: currentCardPosition.left,
    }"
  >
    <!-- eslint-disable-next-line -->
    <typography-component v-html="currentCard.content" variant="body-large" color="#2A3E50" />
    <BoxComponent :mt="24">
      <FlexComponent justify="end">
        <ButtonComponent variant="accent" @click="setNextCard">{{ currentCard.buttonText || 'Got it' }}</ButtonComponent>
      </FlexComponent>
    </BoxComponent>
  </div>
</template>

<style scoped lang="scss">
.card {
  position: absolute;
  z-index: 10001;
  padding: 16px 24px;

  border: 2px solid var(--pale-lavender-300);
  border-radius: 16px;
  background: var(--pale-lavender-500);

  &.is-fixed {
    position: fixed;
  }

  &.is-hidden {
    display: none;
  }
}
</style>
